import React, {useState, useEffect, useMemo} from 'react'
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import { CardActions } from '@mui/material';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import { Button } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from "react-query";
import apiClient from '../../request/http-common';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useParams } from 'react-router-dom';

const DEFAULT_PAYMENT_SOURCES = [
  'Overall Asset',
  'Accumulated Profit',
  'Withdrawable Balance',
  'Funding Wallet',
  'Referral Wallet',
];

const EditInvestment = () => {
    const params = useParams();
    const queryClient = useQueryClient();
    const [investment, setInvestment] = useState(null);
    const [investmentPlans, setInvestmentPlans] = useState([]);
    const [error, setErros] = useState('');
    const [investmentForm, setinvestmentForm] = useState({
        amount_invested: '',
        payment_source: '',
        status: 'pending',
        earning: '',
        duration: '',
        investment_plan_id: '',
    });

    const { isLoading: isLoadingInvestment } = useQuery(
        ["investment-detail", params.id],
        async () => {
          return await apiClient.get(`/api/get-investment/${params.id}`);
        },
        {
          enabled: !!params.id,
          onSuccess: (res) => {
            if (res.data.status === 'success') {
                const data = res.data.investment;
                setInvestment(data);
                setinvestmentForm({
                    amount_invested: data.amount_invested ?? '',
                    payment_source: data.payment_source ?? '',
                    status: data.status ?? 'pending',
                    earning: data.earning ?? '',
                    duration: data.duration ?? '',
                    investment_plan_id: data.investment_plan_id != null ? String(data.investment_plan_id) : '',
                });
                const event2 = new Event('processed');
                window.dispatchEvent(event2);
            }
          },
          onError: (err) => {
            console.log(err.response?.data || err);
            const event2 = new Event('processed');
            window.dispatchEvent(event2);
          },
        }
      );

    const { isLoading: isLoadinginvestments } = useQuery(
        "investment-plans",
        async () => {
          return await apiClient.get(`/api/investment-plans`);
        },
        {
          enabled: true,
          onSuccess: (res) => {
            if (res.data.status === 'success') {
                setInvestmentPlans(res.data.plans || []);
            }
          },
        }
      );

    const selectedPlan = useMemo(() => {
        if (!investmentForm.investment_plan_id || !investmentPlans.length) {
            return null;
        }
        return investmentPlans.find(
            (plan) => String(plan.id) === String(investmentForm.investment_plan_id)
        ) || null;
    }, [investmentForm.investment_plan_id, investmentPlans]);

    const durationOptions = useMemo(() => {
        const options = new Set();
        if (investmentForm.duration) {
            options.add(String(investmentForm.duration).trim());
        }
        if (selectedPlan?.duration) {
            selectedPlan.duration.split(',').forEach((item) => {
                const trimmed = item.trim();
                if (trimmed) {
                    options.add(trimmed);
                }
            });
        }
        return Array.from(options);
    }, [selectedPlan, investmentForm.duration]);

    const paymentSources = useMemo(() => {
        const sources = new Set(DEFAULT_PAYMENT_SOURCES);
        if (investmentForm.payment_source) {
            sources.add(investmentForm.payment_source);
        }
        return Array.from(sources);
    }, [investmentForm.payment_source]);

    const onChange = (e) => {
        const { name, value } = e.target;
        setinvestmentForm((prev) => ({ ...prev, [name]: value }));
    };

    const { isLoading: isSendingRequest, mutate: postinvestment } = useMutation(
        async () => {
          return await apiClient.post(`/api/edit-investment/${params.id}`, {
            amount : investmentForm.amount_invested,	
            payment_source : investmentForm.payment_source,	
            status : investmentForm.status,	
            earning : investmentForm.earning,	
            duration : investmentForm.duration,	
            investment_plan_id : investmentForm.investment_plan_id,	
          });
        },
        {
          onSuccess: (res) => {
            if (res.data?.investment) {
                setInvestment(res.data.investment);
                setinvestmentForm({
                    amount_invested: res.data.investment.amount_invested ?? '',
                    payment_source: res.data.investment.payment_source ?? '',
                    status: res.data.investment.status ?? 'pending',
                    earning: res.data.investment.earning ?? '',
                    duration: res.data.investment.duration ?? '',
                    investment_plan_id: res.data.investment.investment_plan_id != null
                        ? String(res.data.investment.investment_plan_id)
                        : '',
                });
            }
            queryClient.invalidateQueries('list-investments');
            queryClient.invalidateQueries(["investment-detail", params.id]);
            localStorage.setItem('notification', JSON.stringify({message : `${res.data.message}`, 'type': 'success' }));
            const event = new Event('newMessage');
            window.dispatchEvent(event);
            const event2 = new Event('processed');
            window.dispatchEvent(event2);
          },
          onError: (err) => {   
            let myerror = err.response?.data || err;         
            setErros(myerror.errors || myerror)  
            const event2 = new Event('processed');
            window.dispatchEvent(event2);     
          },
        }
      );

      const onSubmit = () =>{
        const event3 = new Event('processing');
        window.dispatchEvent(event3);
        postinvestment();
      } 

      useEffect(() => {
        if (params.id) {
            const event3 = new Event('processing');
            window.dispatchEvent(event3);
        }
      }, [params.id]);

  return (
    <div style={{marginTop: '70px'}}>
        {(isLoadingInvestment || isLoadinginvestments) ? (
            <Grid container px={2} mt={4} mb={4} justifyContent="center">
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading investment data...</Typography>
            </Grid>
        ) : investment !== null ? (
            <Grid container px={2} mt={4}  mb={4} rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                <Grid item xs={12} md={12}>
                    <Card sx={{marginX: '5px', borderRadius: '9px', height: '100%'}}>
                        <CardContent>
                        <Typography mt={4} sx={{ fontWeight: 'bold', fontSize: 24, textAlign: 'center'  }} gutterBottom variant="h6" component="div">
                            EDIT INVESTMENT #{investment.id}
                        </Typography>
                        <Typography  sx={{  textAlign: 'center', fontSize: 16  }} variant="subtitle1" gutterBottom>
                            Fill the form below to edit investment for {investment.user?.name}
                        </Typography>
                        
                      <FormControl sx={{ m: 1, minWidth: '100%' }} size="small">
                          <InputLabel htmlFor="payment_source">Select Payment Source</InputLabel>
                          <Select
                              onChange={onChange}
                              labelId="payment_source"
                              name="payment_source"
                              id="payment_source"
                              value={investmentForm.payment_source}
                              label={`Select Payment Source`}
                              MenuProps={{
                                  PaperProps: {
                                      style: {
                                          maxHeight: 200,
                                          zIndex: 9999,
                                      },
                                  },
                              }}
                              >
                              <MenuItem value=''>
                              Select Gateway
                              </MenuItem> 
                              {paymentSources.map((source) => (
                                <MenuItem key={source} value={source}>
                                  {source}
                                </MenuItem>
                              ))}
                          </Select>
                          <Typography color='error'  sx={{  textAlign: 'left', fontSize: 16  }} variant="subtitle1" gutterBottom>
                          {error !== '' && error.payment_source}
                          </Typography>
                      </FormControl>

                        <FormControl fullWidth={true} sx={{ m: 1, minWidth: 50 }}>
                            <InputLabel htmlFor="amount_invested">Amount Invested</InputLabel>
                            <OutlinedInput
                            onChange={onChange}
                            error={Boolean(error !== '' && error.amount)}
                            id="amount_invested"
                            value={investmentForm.amount_invested}
                            type={'number'}
                            name="amount_invested"
                            label="Amount Invested"
                            />
                            <Typography color='error'  sx={{  textAlign: 'left', fontSize: 16  }} variant="subtitle1" gutterBottom>
                            {error !== '' && (error.amount || error.amount_invested)}
                            </Typography>
                        </FormControl>

                        <FormControl fullWidth={true} sx={{ m: 1, minWidth: 50 }}>
                            <InputLabel htmlFor="earning">Earning</InputLabel>
                            <OutlinedInput
                            onChange={onChange}
                            error={Boolean(error !== '' && error.earning)}
                            id="earning"
                            type={'number'}
                            value={investmentForm.earning}
                            name="earning"
                            label="Earning"
                            />
                            <Typography color='error'  sx={{  textAlign: 'left', fontSize: 16  }} variant="subtitle1" gutterBottom>
                            {error !== '' && error.earning}
                            </Typography>
                        </FormControl>

                        <FormControl sx={{ m: 1, minWidth: '100%' }} size="small">
                            <InputLabel htmlFor="duration">Duration</InputLabel>
                            <Select
                                onChange={onChange}
                                labelId="duration"
                                id="duration"
                                name="duration"
                                value={investmentForm.duration}
                                label="Duration"
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 200,
                                            zIndex: 9999,
                                        },
                                    },
                                }}
                            >
                                <MenuItem value="">
                                    Select duration
                                </MenuItem>
                                {durationOptions.map((duration) => (
                                    <MenuItem key={duration} value={duration}>
                                        {duration}
                                    </MenuItem>
                                ))}
                            </Select>
                            <Typography color='error'  sx={{  textAlign: 'left', fontSize: 16  }} variant="subtitle1" gutterBottom>
                            {error !== '' && error.duration}
                            </Typography>
                        </FormControl>

                        {investmentPlans.length > 0 &&
                        <FormControl sx={{ m: 1, minWidth: '100%' }} size="small">
                        <InputLabel htmlFor="investment_plan_id">Investment Plan</InputLabel>
                        <Select
                            onChange={onChange}
                            labelId="investment_plan_id"
                            name="investment_plan_id"
                            id="investment_plan_id"
                            value={investmentForm.investment_plan_id}
                            label={`Investment Plan`}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 200,
                                        zIndex: 9999,
                                    },
                                },
                            }}
                            >
                            <MenuItem value="">
                                Investment Plan
                            </MenuItem>              
                             {investmentPlans.map(plan =>(
                            <MenuItem key={plan.id} value={String(plan.id)}>{plan.name}</MenuItem>
                            ))}   
                        </Select>
                        <Typography color='error'  sx={{  textAlign: 'left', fontSize: 16  }} variant="subtitle1" gutterBottom>
                        {error !== '' && error.investment_plan_id}
                        </Typography>
                    </FormControl>
                        }

            <FormControl fullWidth={true} sx={{ m: 1, minWidth: 50 }}>
            <InputLabel htmlFor="status">status</InputLabel>
                <Select
                    onChange={onChange}
                    value={investmentForm.status}
                    labelId="status"
                    id="status"
                    name="status"
                    label='status'
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 200,
                                zIndex: 9999,
                            },
                        },
                    }}
                    >
                    <MenuItem value=''>
                        select status
                    </MenuItem> 
                    <MenuItem value="pending">pending</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="declined">Declined</MenuItem>
                    <MenuItem value="completed">completed</MenuItem>
                </Select>
            </FormControl>
                        
                    </CardContent>   

                    <CardActions>
                        <Button onClick={onSubmit} fullWidth={true} size="large" color="primary" variant="contained"> {isSendingRequest? 'Updating...' : 'Update investment'}</Button>
                    </CardActions>
                </Card>
            </Grid>
        </Grid>
        ) : (
            <Grid container px={2} mt={4} mb={4} justifyContent="center">
                <Typography variant="h6" color="error">
                    Investment not found or failed to load.
                </Typography>
            </Grid>
        )}
    </div>
  )
}

export default EditInvestment
